import { createSelector, Selector } from '@reduxjs/toolkit'
import { ColDef, ColGroupDef, GridOptions } from 'ag-grid-community'
import { isEmpty } from 'lodash-es'
import { GlobalState } from 'store'
import {
  commonGridLeftColumnDefs,
  counterpartyGridColumnDefs,
  nonCounterpartyColumnDefs,
  commonGridRightColumnDefs
} from 'view/routes/requests/initiated/InitiatedNegotiationsGridModel'
import { gridOptionsBase } from 'view/components/datagrid'
import {
  createInitiatedCounterpartyColumnDetailSelector,
  createUserInitiatedNegotiationsSelector,
  groupedByCounterpartyIdInitiatedNegotiationsSelector
} from './negotiationSelectors'
import { createMapCounterpartyColumns, getNegotiationId } from './negotiationGridSelectorsUtil'
import { createIsInitiatedNegotiationCounterpartyViewEnabled } from '../userPreferenceSelectors'
import { Negotiation, NegotiationDetail, NegotiationPackageDetail } from 'api'
import { isObject } from 'lodash-es'
import { GridCounterPartyHeader } from 'view/routes/requests/initiated/GridCounterPartyHeader'
import { InitiatedPreAssignmentsSubGrid } from 'view/routes/requests/initiated/InitiatedPreAssignmentsSubGrid'
import { hasNegotiationNestedLegs } from 'view/shared/datagrid'
import { getDynamicDetailRowHeight } from 'view/shared/preAssignments'

export const createCounterpartyColumnDefsSelector = (): Selector<GlobalState, ColGroupDef[]> =>
  createSelector(createInitiatedCounterpartyColumnDetailSelector(), counterparties => {
    return counterparties.map(counterparty => ({
      headerName: counterparty.headerName,
      headerGroupComponent: GridCounterPartyHeader,
      headerGroupComponentParams: {
        counterpartyId: counterparty.id
      },
      marryChildren: true,
      children: counterpartyGridColumnDefs.map(createMapCounterpartyColumns(counterparty.id)),
      headerClass: 'header-cell--grouped'
    }))
  })

const hiddenNegotiableFields: (keyof Negotiation)[] = ['endDate', 'isAxed', 'notional', 'startConsideration', 'startDate', 'term']

export const createModifiedCounterPartySelector = (counterpartyId: string): Selector<GlobalState, boolean> =>
  createSelector(groupedByCounterpartyIdInitiatedNegotiationsSelector, groupedCounterpartyNegotiations => {
    if (groupedCounterpartyNegotiations[counterpartyId] === undefined) {
      return false
    }

    const isModified = groupedCounterpartyNegotiations[counterpartyId].some(({ negotiation }) => {
      return hiddenNegotiableFields.some(fieldKey => {
        const field = negotiation[fieldKey]
        if (isObject(field) && 'initiatorLastValue' in field) {
          const hasResponderModified =
            field.original !== field.negotiated &&
            field.initiatorLastValue !== field.negotiated &&
            field.initiatorLastValue !== field.responderLastValue

          return hasResponderModified
        }

        return false
      })
    })

    return isModified
  })

export const createInitiatedPackagesColumnDefsSelector = (): Selector<GlobalState, (ColGroupDef | ColDef)[]> =>
  createSelector(createCounterpartyColumnDefsSelector(), counterpartyColDefs =>
    isEmpty(counterpartyColDefs)
      ? counterpartyColDefs
      : commonGridLeftColumnDefs.concat(counterpartyColDefs).concat(commonGridRightColumnDefs)
  )

export const initiatedColumnDefsSelector: Selector<
  GlobalState,
  (ColGroupDef | ColDef)[]
> = createSelector(
  createInitiatedPackagesColumnDefsSelector(),
  createIsInitiatedNegotiationCounterpartyViewEnabled(),
  (counterpartyColDefs, isCounterpartyViewEnabled) => (isCounterpartyViewEnabled ? counterpartyColDefs : nonCounterpartyColumnDefs)
)

export const initiatedNegotiationsGridOptions: Selector<
  GlobalState,
  GridOptions<NegotiationPackageDetail | NegotiationDetail>
> = createSelector(
  createIsInitiatedNegotiationCounterpartyViewEnabled(),
  createUserInitiatedNegotiationsSelector,
  (isCounterpartyViewEnabled, initiatedNegotiations) => {
    const containsCollections = initiatedNegotiations.some((negotiationDetail: NegotiationDetail) => negotiationDetail.collection)
    const headerHeight = isCounterpartyViewEnabled ? 20 : 19

    const gridOptions: GridOptions<NegotiationPackageDetail | NegotiationDetail> = {
      ...gridOptionsBase,
      defaultColDef: {
        ...gridOptionsBase.defaultColDef,
        enableRowGroup: !isCounterpartyViewEnabled
      } as ColDef<NegotiationPackageDetail | NegotiationDetail>,
      groupHeaderHeight: headerHeight,
      headerHeight,
      groupDefaultExpanded: isCounterpartyViewEnabled || !containsCollections ? gridOptionsBase.groupDefaultExpanded : 1,
      getRowId: ({ data }) => getNegotiationId(data),
      masterDetail: true,
      getRowHeight: getDynamicDetailRowHeight((data: NegotiationDetail) => data?.negotiation?.nestedLegs?.legs?.length),
      isRowMaster: negotiationDetail => hasNegotiationNestedLegs(negotiationDetail) && !isCounterpartyViewEnabled,
      detailCellRenderer: InitiatedPreAssignmentsSubGrid
    }

    return gridOptions
  }
)
